import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Activity, ArrowUpRight, BarChart3, CheckCircle2, ChevronDown, ChevronRight,
  Download, PlayCircle, RefreshCw, StopCircle, Target, Zap,
} from "lucide-react";
import { api, API_ORIGIN } from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";

const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata", day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso; }
};

const Pct = ({ value }) => {
  if (value == null) return <span className="text-muted-3">—</span>;
  const v = Number(value);
  const color = v > 0 ? "var(--bull)" : v < 0 ? "var(--bear)" : "var(--muted-2)";
  return <span style={{ color }}>{v > 0 ? "+" : ""}{v.toFixed(2)}%</span>;
};

const Card = ({ label, value, testid, color = "var(--muted-1)" }) => (
  <div
    className="border border-grid p-3 flex flex-col gap-1"
    data-testid={testid}
  >
    <div className="text-[9px] uppercase tracking-[0.25em] text-muted-3 font-mono-data">
      {label}
    </div>
    <div className="text-xl font-mono-data" style={{ color }}>
      {value ?? "—"}
    </div>
  </div>
);

const Toggle = ({ label, checked, onChange, testid, disabled = false }) => (
  <label
    className="flex items-center gap-2 cursor-pointer select-none"
    data-testid={`${testid}-label`}
  >
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      data-testid={testid}
      className="accent-emerald-500 h-3.5 w-3.5"
    />
    <span className="text-[11px] uppercase tracking-[0.2em] font-mono-data text-muted-1">
      {label}
    </span>
  </label>
);

const NearMissPanel = ({ results }) => {
  const [open, setOpen] = useState(true);
  const nearMisses = results?.near_misses || [];
  const floor = results?.near_miss_floor ?? 70;
  const minScore = results?.min_score ?? 80;

  return (
    <div className="border border-grid" data-testid="la-near-miss-panel">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 border-b border-grid hover:bg-white/[0.02]"
        data-testid="la-near-miss-toggle"
      >
        <div className="flex items-center gap-2">
          <Target size={14} style={{ color: "var(--warn, #d19a66)" }} />
          <div className="font-heading text-sm uppercase tracking-wider">
            Near-Miss Picks
          </div>
          <span
            className="text-[10px] font-mono-data px-2 py-0.5 border"
            style={{ borderColor: "var(--warn, #d19a66)", color: "var(--warn, #d19a66)" }}
          >
            {nearMisses.length}
          </span>
          <span className="text-[10px] font-mono-data text-muted-3">
            Score ∈ [{floor}, {minScore})
          </span>
        </div>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <div>
          {nearMisses.length === 0 ? (
            <div className="px-3 py-4 text-xs text-muted-3 text-center font-mono-data" data-testid="la-near-miss-empty">
              No near-misses. All qualifying picks are in the main table (score ≥ {minScore}).
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] font-mono-data">
                <thead>
                  <tr className="text-[9px] uppercase tracking-wider text-muted-3 border-b border-grid">
                    <th className="text-left px-2 py-2">Time</th>
                    <th className="text-left px-2 py-2">Stock</th>
                    <th className="text-right px-2 py-2">Price</th>
                    <th className="text-right px-2 py-2">Score</th>
                    <th className="text-right px-2 py-2">Gap to Qualify</th>
                    <th className="text-right px-2 py-2">ADX</th>
                    <th className="text-right px-2 py-2">RSI</th>
                    <th className="text-right px-2 py-2">Vol×</th>
                    <th className="text-left px-2 py-2">Missing Gates</th>
                  </tr>
                </thead>
                <tbody>
                  {nearMisses.map((p) => (
                    <tr
                      key={`nm-${p.symbol}`}
                      className="border-b border-grid/40"
                      data-testid={`la-near-miss-${p.symbol}`}
                    >
                      <td className="px-2 py-1.5">{p.scan_time_ist}</td>
                      <td className="px-2 py-1.5 font-bold">{p.symbol}</td>
                      <td className="px-2 py-1.5 text-right">{p.indicators?.close}</td>
                      <td className="px-2 py-1.5 text-right" style={{ color: "var(--warn, #d19a66)" }}>
                        {p.score}
                      </td>
                      <td className="px-2 py-1.5 text-right text-muted-2">
                        −{minScore - p.score}
                      </td>
                      <td className="px-2 py-1.5 text-right">{p.indicators?.adx}</td>
                      <td className="px-2 py-1.5 text-right">{p.indicators?.rsi}</td>
                      <td className="px-2 py-1.5 text-right">{p.indicators?.rvol}</td>
                      <td className="px-2 py-1.5 text-muted-3 text-[10px]">
                        {(p.missing_gates || []).join(", ") || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const BacktestPanel = () => {
  const [symbols, setSymbols] = useState("");
  const [days, setDays] = useState(5);
  const [limit, setLimit] = useState(50);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [job, setJob] = useState(null);       // Active background job
  const [history, setHistory] = useState([]);
  const pollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/jobs?kind=linear_accumulation_backtest&limit=20");
      setHistory(data?.jobs || []);
    } catch { /* history is best-effort */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPolling = (jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        setJob(data);
        if (["success", "failed", "cancelled"].includes(data?.status)) {
          stopPolling();
          setBusy(false);
          if (data.status === "success") {
            setResult(data.result || {});
            toast.success(`Backtest complete · ${data.result?.total_signals ?? 0} signals · ${data.result?.win_pct ?? 0}% win`);
          } else if (data.status === "failed") {
            toast.error(`Backtest failed: ${data.error || "unknown"}`);
          } else {
            toast.info("Backtest cancelled");
          }
          loadHistory();
        }
      } catch { /* transient polling error is fine — retry next tick */ }
    }, 2000);
  };

  const cancelJob = async () => {
    if (!job?.job_id) return;
    try {
      await api.post(`/jobs/${job.job_id}/cancel`);
      toast.info("Cancellation requested…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const run = async () => {
    setBusy(true);
    setResult(null);
    setJob(null);
    try {
      const list = symbols
        .split(",")
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);
      const clampLimit = Math.max(1, Math.min(500, Number(limit) || 50));
      const { data } = await api.post(
        "/steady-intraday-trend/backtest",
        { symbols: list.length ? list : null, days: Number(days), limit: clampLimit },
        { timeout: 300000 }
      );
      if (data?.mode === "sync") {
        setResult(data);
        setBusy(false);
        toast.success(`Backtest complete · ${data.total_signals} signals · ${data.win_pct}% win`);
        loadHistory();
      } else if (data?.mode === "async") {
        setJob(data.job);
        toast.info(`Job ${data.job_id} started — polling for progress…`);
        startPolling(data.job_id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backtest failed");
      setBusy(false);
    }
  };

  useEffect(() => () => stopPolling(), []);

  return (
    <div className="border border-grid p-4 space-y-3" data-testid="la-backtest-panel">
      <div className="flex items-center gap-2">
        <BarChart3 size={16} />
        <h3 className="font-heading text-sm uppercase tracking-wider">
          Historical Backtest
        </h3>
      </div>
      <div className="text-[11px] text-muted-3">
        Replay the Steady Intraday Trend engine on 5-min bars.
        Leave <b>Symbols</b> empty to backtest the first <b>Limit</b> F&amp;O
        universe stocks (~215 symbols).
        Runs ≤ 100 return inline; larger runs execute as a background job with progress + cancel.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data mb-1">
            Symbols (comma-separated)
          </div>
          <input
            value={symbols}
            onChange={(e) => setSymbols(e.target.value)}
            placeholder="RELIANCE, TCS, HDFCBANK"
            data-testid="la-backtest-symbols"
            className="w-full bg-transparent border border-grid px-3 py-1.5 text-xs font-mono-data"
          />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data mb-1">
            Days
          </div>
          <input
            type="number"
            min={1}
            max={30}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            data-testid="la-backtest-days"
            className="w-full bg-transparent border border-grid px-3 py-1.5 text-xs font-mono-data"
          />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data mb-1">
            Limit (max 500)
          </div>
          <input
            type="number"
            min={1}
            max={500}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            data-testid="la-backtest-limit"
            className="w-full bg-transparent border border-grid px-3 py-1.5 text-xs font-mono-data"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={run}
            disabled={busy}
            data-testid="la-backtest-run"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 border text-[11px] uppercase tracking-[0.2em] font-mono-data"
            style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
          >
            {busy ? <RefreshCw size={13} className="animate-spin" /> : <PlayCircle size={13} />}
            {busy ? (job ? "Running (job)…" : "Running…") : "Run Backtest"}
          </button>
        </div>
      </div>

      {/* Active Job progress */}
      {job && (
        <div
          className="pt-3 border-t border-grid space-y-2"
          data-testid="la-backtest-job"
        >
          <div className="flex items-center justify-between gap-2 text-[11px] font-mono-data">
            <div className="flex items-center gap-2">
              <span className="text-muted-3 text-[10px] uppercase tracking-wider">Job</span>
              <span className="font-bold">{job.job_id}</span>
              <span
                className="text-[9px] uppercase px-2 py-0.5 border"
                style={{
                  color: job.status === "running" ? "var(--warn, #d19a66)"
                       : job.status === "success" ? "var(--bull)"
                       : job.status === "failed" ? "var(--bear)"
                       : "var(--muted-3)",
                  borderColor: "currentColor",
                }}
              >
                {job.status}
              </span>
              <span className="text-muted-3">{job.name}</span>
            </div>
            {["queued", "running"].includes(job.status) && (
              <button
                onClick={cancelJob}
                data-testid="la-backtest-cancel"
                className="inline-flex items-center gap-1 px-2 py-1 border text-[10px] uppercase font-mono-data"
                style={{ color: "var(--bear)", borderColor: "var(--bear)" }}
              >
                <StopCircle size={12} /> Cancel
              </button>
            )}
          </div>
          <div className="w-full h-2 bg-white/[0.05] border border-grid overflow-hidden">
            <div
              className="h-full"
              style={{
                width: `${job.progress || 0}%`,
                backgroundColor: "var(--bull)",
                transition: "width 0.5s ease",
              }}
              data-testid="la-backtest-progress"
            />
          </div>
          <div className="flex justify-between text-[10px] text-muted-3 font-mono-data">
            <span>{(job.progress || 0).toFixed(1)}%</span>
            <span>
              {job.eta_seconds != null ? `ETA ${Math.max(0, Math.round(job.eta_seconds))} s` : "…"}
            </span>
          </div>
        </div>
      )}
      {result && (
        <div className="pt-3 border-t border-grid space-y-2" data-testid="la-backtest-result">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <Card label="Symbols" value={result.symbols_ok} testid="la-bt-symbols" />
            <Card label="Signals" value={result.total_signals} testid="la-bt-signals" />
            <Card label="Near Miss" value={result.near_miss_signals} testid="la-bt-near-miss" color="var(--warn, #d19a66)" />
            <Card label="Wins" value={result.total_wins} testid="la-bt-wins" color="var(--bull)" />
            <Card label="Losses" value={result.total_losses} testid="la-bt-losses" color="var(--bear)" />
            <Card label="Win %" value={`${result.win_pct}%`} testid="la-bt-winpct" />
          </div>
          <div className="text-right text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
            Total P&L: <Pct value={result.total_pnl_pct} />
          </div>
          <div className="max-h-72 overflow-auto border border-grid">
            <table className="w-full text-[11px] font-mono-data">
              <thead>
                <tr className="text-[9px] uppercase tracking-wider text-muted-3 border-b border-grid">
                  <th className="text-left px-2 py-1.5">Symbol</th>
                  <th className="text-right px-2 py-1.5">Signals</th>
                  <th className="text-right px-2 py-1.5">Near Miss</th>
                  <th className="text-right px-2 py-1.5">Wins</th>
                  <th className="text-right px-2 py-1.5">Win %</th>
                  <th className="text-right px-2 py-1.5">Total P&L %</th>
                </tr>
              </thead>
              <tbody>
                {(result.per_symbol || []).filter((r) => r.ok).map((r) => (
                  <tr key={r.symbol} className="border-b border-grid/40">
                    <td className="px-2 py-1">{r.symbol}</td>
                    <td className="px-2 py-1 text-right">{r.signals}</td>
                    <td className="px-2 py-1 text-right" style={{ color: "var(--warn, #d19a66)" }}>
                      {r.near_miss_signals || 0}
                    </td>
                    <td className="px-2 py-1 text-right">{r.wins}</td>
                    <td className="px-2 py-1 text-right">{r.win_pct}%</td>
                    <td className="px-2 py-1 text-right"><Pct value={r.total_pnl_pct} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Backtest history (last 20) */}
      {history.length > 0 && (
        <div className="pt-3 border-t border-grid space-y-2" data-testid="la-backtest-history">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-3 font-mono-data">
            Recent Backtests
          </div>
          <div className="border border-grid overflow-x-auto">
            <table className="w-full text-[11px] font-mono-data">
              <thead>
                <tr className="text-[9px] uppercase tracking-wider text-muted-3 border-b border-grid">
                  <th className="text-left px-2 py-1.5">Job</th>
                  <th className="text-left px-2 py-1.5">Name</th>
                  <th className="text-left px-2 py-1.5">Status</th>
                  <th className="text-right px-2 py-1.5">Signals</th>
                  <th className="text-right px-2 py-1.5">Win %</th>
                  <th className="text-right px-2 py-1.5">P&L %</th>
                  <th className="text-right px-2 py-1.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr
                    key={h.job_id}
                    className="border-b border-grid/40"
                    data-testid={`la-history-${h.job_id}`}
                  >
                    <td className="px-2 py-1.5">{h.job_id}</td>
                    <td className="px-2 py-1.5 text-muted-2">{h.name}</td>
                    <td className="px-2 py-1.5">
                      <span
                        className="text-[9px] uppercase px-1.5 py-0.5 border"
                        style={{
                          color: h.status === "success" ? "var(--bull)"
                               : h.status === "failed" ? "var(--bear)"
                               : "var(--muted-2)",
                          borderColor: "currentColor",
                        }}
                      >
                        {h.status}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-right">{h.result?.total_signals ?? "—"}</td>
                    <td className="px-2 py-1.5 text-right">{h.result?.win_pct != null ? `${h.result.win_pct}%` : "—"}</td>
                    <td className="px-2 py-1.5 text-right">
                      {h.result?.total_pnl_pct != null ? <Pct value={h.result.total_pnl_pct} /> : "—"}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {h.status === "success" && (
                        <a
                          href={`${API_ORIGIN}/api/jobs/${h.job_id}/export.csv`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`la-history-csv-${h.job_id}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase text-muted-1 hover:text-white"
                        >
                          <Download size={11} /> CSV
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export const LinearAccumulationPage = () => {
  const [results, setResults] = useState(null);
  const [summary, setSummary] = useState(null);
  const [trades, setTrades] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s, p, c] = await Promise.all([
        api.get("/steady-intraday-trend"),
        api.get("/steady-intraday-trend/summary"),
        api.get("/steady-intraday-trend/paper?days=1"),
        api.get("/admin/config/section_steady_intraday_trend")
          .catch(() => api.get("/admin/config/section_linear_accumulation").catch(() => ({ data: null }))),
      ]);
      setResults(r.data);
      setSummary(s.data);
      setTrades(p.data?.trades || []);
      if (c?.data) setCfg(c.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Steady Intraday Trend");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (refreshInterval > 0) {
      timerRef.current = setInterval(load, refreshInterval * 1000);
    }
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [refreshInterval, load]);

  const toggleFlag = async (key, next) => {
    if (!cfg) return;
    const nextValues = { ...(cfg.values || cfg.defaults || {}), [key]: next ? 1 : 0 };
    try {
      await api.post("/admin/config/section_steady_intraday_trend", {
        values: nextValues,
        enabled: cfg.enabled ?? true,
      });
      toast.success(`${key} → ${next ? "ON" : "OFF"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed (admin only)");
    }
  };

  const runNow = async () => {
    setRunning(true);
    toast.info("Running scan now…");
    try {
      const { data } = await api.post("/steady-intraday-trend/scan", null, { timeout: 120000 });
      toast.success(
        `Scan done · ${data?.total ?? 0} qualified · ${data?.near_miss_count ?? 0} near-miss · ${(data?.auto_entered || []).length} paper trades`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setRunning(false);
    }
  };

  const flags = useMemo(() => {
    const v = cfg?.values || cfg?.defaults || {};
    return {
      scanner_enabled: !!v.scanner_enabled,
      paper_trading_enabled: !!v.paper_trading_enabled,
      live_trading_enabled: !!v.live_trading_enabled,
      alerts_enabled: !!v.alerts_enabled,
      option_reco_enabled: !!v.option_reco_enabled,
    };
  }, [cfg]);

  const qualifying = results?.matches || results?.qualifying || [];

  return (
    <section className="space-y-5" data-testid="steady-intraday-trend-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
            Auto Picks · S14
          </div>
          <h2 className="font-heading text-2xl flex items-center gap-2">
            <ArrowUpRight size={20} /> Steady Intraday Trend Scanner
          </h2>
          <p className="text-xs text-muted-3 mt-1">
            F&amp;O universe (~215 stocks) · every 5 min · price-action first —
            structure, VWAP strength and trend smoothness. Paper by default;
            Live gated on admin toggle.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data flex items-center gap-2">
            Refresh
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              data-testid="la-refresh-interval"
              className="bg-transparent border border-grid px-2 py-1 text-xs font-mono-data"
            >
              <option value={0}>Off</option>
              <option value={30}>30 s</option>
              <option value={60}>60 s</option>
              <option value={300}>5 min</option>
            </select>
          </label>
          <button
            onClick={load}
            disabled={loading}
            data-testid="la-refresh-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-grid text-[11px] uppercase tracking-[0.2em] font-mono-data hover:text-white"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Warning banner */}
      {results?.warning && (
        <div
          className="border px-3 py-2 text-[11px] font-mono-data"
          style={{ borderColor: "var(--warn, #d19a66)", color: "var(--warn, #d19a66)" }}
          data-testid="la-warning-banner"
        >
          {results.warning}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
        <Card label="Stocks Scanned" value={results?.universe_size} testid="la-summary-scanned" />
        <Card label="Qualified" value={results?.total} testid="la-summary-qualified" color="var(--bull)" />
        <Card label="Paper Trades Today" value={summary?.paper_trades_today} testid="la-summary-paper" />
        <Card label="Live Trades Today" value={summary?.live_trades_today} testid="la-summary-live" color={summary?.live_trades_today ? "var(--bull)" : "var(--muted-3)"} />
        <Card label="Telegram Alerts" value={summary?.paper_trades_today} testid="la-summary-alerts" />
        <Card label="Avg Score" value={summary?.avg_score} testid="la-summary-avg-score" />
        <Card label="Today P&L (Paper)" value={<Pct value={summary?.total_pnl_pct} />} testid="la-summary-paper-pnl" />
        <Card label="Today P&L (Live)" value={<Pct value={summary?.live_total_pnl_pct} />} testid="la-summary-live-pnl" />
      </div>

      {/* Scanner Controls */}
      {cfg ? (
        <div className="border border-grid p-4 space-y-3" data-testid="la-controls">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-heading text-sm uppercase tracking-wider">
              Scanner Controls
            </h3>
            <div className="flex gap-2">
              <button
                onClick={runNow}
                disabled={running}
                data-testid="la-run-now"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border text-[11px] uppercase tracking-[0.2em] font-mono-data"
                style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
              >
                {running ? <RefreshCw size={13} className="animate-spin" /> : <Zap size={13} />}
                {running ? "Running…" : "Run Now"}
              </button>
              <button
                onClick={() => toggleFlag("scanner_enabled", !flags.scanner_enabled)}
                data-testid="la-stop-scanner"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-grid text-[11px] uppercase tracking-[0.2em] font-mono-data"
              >
                <StopCircle size={13} />
                {flags.scanner_enabled ? "Stop Scanner" : "Start Scanner"}
              </button>
              <a
                href={`${API_ORIGIN}/api/steady-intraday-trend/export.csv?days=7`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="la-export-csv"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-grid text-[11px] uppercase tracking-[0.2em] font-mono-data hover:text-white"
              >
                <Download size={13} /> Export CSV
              </a>
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <Toggle label="Enable Scanner" checked={flags.scanner_enabled}
                    onChange={(v) => toggleFlag("scanner_enabled", v)} testid="la-toggle-scanner" />
            <Toggle label="Paper Trading" checked={flags.paper_trading_enabled}
                    onChange={(v) => toggleFlag("paper_trading_enabled", v)} testid="la-toggle-paper" />
            <Toggle label="Live Trading" checked={flags.live_trading_enabled}
                    onChange={(v) => toggleFlag("live_trading_enabled", v)} testid="la-toggle-live" />
            <Toggle label="Telegram Alerts" checked={flags.alerts_enabled}
                    onChange={(v) => toggleFlag("alerts_enabled", v)} testid="la-toggle-alerts" />
            <Toggle label="Option Recommendation" checked={flags.option_reco_enabled}
                    onChange={(v) => toggleFlag("option_reco_enabled", v)} testid="la-toggle-option" />
          </div>
        </div>
      ) : (
        <Skeleton className="h-24 w-full" />
      )}

      {/* Qualifying Picks + Trades table */}
      <div className="border border-grid overflow-x-auto" data-testid="la-table">
        <table className="w-full text-[11px] font-mono-data">
          <thead>
            <tr className="text-[9px] uppercase tracking-wider text-muted-3 border-b border-grid">
              <th className="text-left px-2 py-2">Time</th>
              <th className="text-left px-2 py-2">Stock</th>
              <th className="text-right px-2 py-2">Price</th>
              <th className="text-right px-2 py-2">Score</th>
              <th className="text-right px-2 py-2" title="Trend Smoothness (0-100)">Smooth</th>
              <th className="text-right px-2 py-2" title="Higher Highs / Higher Lows in last 8 candles">HH/HL</th>
              <th className="text-right px-2 py-2" title="Max pullback of prior impulse">PB%</th>
              <th className="text-right px-2 py-2">Vol×</th>
              <th className="text-right px-2 py-2">ADX</th>
              <th className="text-right px-2 py-2">RSI</th>
              <th className="text-left px-2 py-2">Option</th>
              <th className="text-right px-2 py-2">Entry</th>
              <th className="text-right px-2 py-2">SL</th>
              <th className="text-right px-2 py-2">Target</th>
              <th className="text-left px-2 py-2">Status</th>
              <th className="text-right px-2 py-2">P/L %</th>
            </tr>
          </thead>
          <tbody>
            {/* Live qualifying picks (not yet in paper trade collection) */}
            {qualifying.map((p) => (
              <tr key={`q-${p.symbol}`} className="border-b border-grid/40" data-testid={`la-pick-${p.symbol}`}>
                <td className="px-2 py-1.5">{p.scan_time_ist}</td>
                <td className="px-2 py-1.5 font-bold">{p.symbol}</td>
                <td className="px-2 py-1.5 text-right">{p.indicators?.close}</td>
                <td className="px-2 py-1.5 text-right" style={{ color: "var(--bull)" }}>{p.score}</td>
                <td className="px-2 py-1.5 text-right">{p.indicators?.smoothness_score ?? "—"}</td>
                <td className="px-2 py-1.5 text-right">{p.indicators?.hh_count ?? "—"}/{p.indicators?.hl_count ?? "—"}</td>
                <td className="px-2 py-1.5 text-right">{p.indicators?.pullback_pct != null ? `${p.indicators.pullback_pct}%` : "—"}</td>
                <td className="px-2 py-1.5 text-right">{p.indicators?.rvol}</td>
                <td className="px-2 py-1.5 text-right">{p.indicators?.adx}</td>
                <td className="px-2 py-1.5 text-right">{p.indicators?.rsi}</td>
                <td className="px-2 py-1.5">{p.option?.strike ? `${p.option.strike} ${p.option.type}` : "—"}</td>
                <td className="px-2 py-1.5 text-right">{p.entry}</td>
                <td className="px-2 py-1.5 text-right" style={{ color: "var(--bear)" }}>{p.sl}</td>
                <td className="px-2 py-1.5 text-right" style={{ color: "var(--bull)" }}>{p.target}</td>
                <td className="px-2 py-1.5 text-[9px] uppercase text-muted-3">SIGNAL</td>
                <td className="px-2 py-1.5 text-right">—</td>
              </tr>
            ))}
            {/* Paper trades today */}
            {trades.map((t) => (
              <tr key={`t-${t._id}`} className="border-b border-grid/40" data-testid={`la-trade-${t._id}`}>
                <td className="px-2 py-1.5">{fmtIST(t.entry_time_ist)}</td>
                <td className="px-2 py-1.5 font-bold">{t.symbol}</td>
                <td className="px-2 py-1.5 text-right">{t.indicators?.close ?? t.entry_price}</td>
                <td className="px-2 py-1.5 text-right">{t.score}</td>
                <td className="px-2 py-1.5 text-right">{t.indicators?.smoothness_score ?? "—"}</td>
                <td className="px-2 py-1.5 text-right">{t.indicators?.hh_count ?? "—"}/{t.indicators?.hl_count ?? "—"}</td>
                <td className="px-2 py-1.5 text-right">{t.indicators?.pullback_pct != null ? `${t.indicators.pullback_pct}%` : "—"}</td>
                <td className="px-2 py-1.5 text-right">{t.indicators?.rvol}</td>
                <td className="px-2 py-1.5 text-right">{t.indicators?.adx}</td>
                <td className="px-2 py-1.5 text-right">{t.indicators?.rsi}</td>
                <td className="px-2 py-1.5">{t.option?.strike ? `${t.option.strike} ${t.option.type}` : "—"}</td>
                <td className="px-2 py-1.5 text-right">{t.entry_price}</td>
                <td className="px-2 py-1.5 text-right" style={{ color: "var(--bear)" }}>{t.sl}</td>
                <td className="px-2 py-1.5 text-right" style={{ color: "var(--bull)" }}>{t.target}</td>
                <td className="px-2 py-1.5">
                  <span className="text-[9px] uppercase" style={{
                    color: t.status === "OPEN" ? "var(--warn, #d19a66)"
                         : (t.pnl_pct || 0) > 0 ? "var(--bull)" : "var(--bear)"
                  }}>
                    {t.status}{t.exit_reason ? ` · ${t.exit_reason}` : ""}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-right"><Pct value={t.pnl_pct} /></td>
              </tr>
            ))}
            {qualifying.length === 0 && trades.length === 0 && (
              <tr>
                <td colSpan={16} className="px-3 py-6 text-center text-muted-3">
                  No qualifying picks or paper trades yet. Scanner runs every {cfg?.values?.scan_interval_min || 5} min during market hours.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Near Miss Panel */}
      <NearMissPanel results={results} />

      {/* Backtest */}
      <BacktestPanel />
    </section>
  );
};

export default LinearAccumulationPage;
